import { useEffect } from 'react';

/**
 * Runs `callback` immediately and then every `intervalMs`, skipping ticks while the tab is
 * hidden and firing once right away when it becomes visible again — avoids burning API/CPU
 * quota polling admin screens left open in a background tab.
 *
 * Pass `enabled: false` (superadmin-configurable global switch) to run `callback` once on
 * mount only, with no recurring timer — callers still get fresh data on load/tab change, just
 * no background refresh; use a manual refresh button to get more.
 */
export function usePolling(callback: () => void, intervalMs: number, enabled = true) {
  useEffect(() => {
    callback();
    if (!enabled) return;
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') callback();
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') callback();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, intervalMs, enabled]);
}
